import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  VariableSelectorInput,
  VariableSelectorInputProps,
} from "../variable-selector-input";
import { Button } from "../button";
import { BoldIcon, ItalicIcon } from "lucide-react";
import { cn } from "@aichat/ui/lib/utils";

type FormatType = "bold" | "italic";

const getWrapper = (type: FormatType) => {
  switch (type) {
    case "bold":
      return "*";
    case "italic":
      return "_";
  }
};

const wrapSelectedOrLastWord = ({
  text,
  selectionStart,
  selectionEnd,
  wrapper,
  mode,
}: {
  text: string;
  selectionStart: number;
  selectionEnd: number;
  wrapper: string;
  mode: "select";
}) => {
  if (mode === "select" && selectionStart !== selectionEnd) {
    const selectedText = text.slice(selectionStart, selectionEnd);
    const newText =
      text.slice(0, selectionStart) +
      wrapper +
      selectedText +
      wrapper +
      text.slice(selectionEnd);
    return {
      newText,
      newStart: selectionStart + wrapper.length,
      newEnd: selectionEnd + wrapper.length,
    };
  }
  return null;
};

type VariableSelectorInputWithToolbarProps = VariableSelectorInputProps & {
  toolbarClassName?: string;
};

export const VariableSelectorInputWithToolbar = forwardRef<
  HTMLTextAreaElement | HTMLInputElement,
  VariableSelectorInputWithToolbarProps
>((props, forwardedRef) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ref = props.singleLine ? inputRef : textAreaRef;

  useImperativeHandle(forwardedRef, () => ref.current!, []);

  const handleFormatClick = (type: FormatType) => {
    const target = ref.current;
    if (!target) return;

    const wrapper = getWrapper(type);

    const result = wrapSelectedOrLastWord({
      text: target.value,
      selectionStart: target?.selectionStart ?? 0,
      selectionEnd: target?.selectionEnd ?? 0,
      wrapper,
      mode: "select",
    });

    if (result) {
      props.onChange(result.newText);
      setTimeout(() => {
        target.setSelectionRange(result.newStart!, result.newEnd!);
      }, 0);
    }

    target.focus();
  };

  return (
    <div>
      <div
        className={cn(
          "flex gap-2 py-2 px-1 border border-gray-200 rounded-tl-md rounded-tr-md relative top-1 pb-3",
          props.toolbarClassName,
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFormatClick("bold")}
        >
          <BoldIcon className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFormatClick("italic")}
        >
          <ItalicIcon className="size-4" />
        </Button>
      </div>
      <VariableSelectorInput ref={ref} {...props} onChange={props.onChange} />
    </div>
  );
});

export default VariableSelectorInputWithToolbar;
